(function () {
  function initReviewVideoCarousel(section) {
    if (!section || section.dataset.reviewVideoCarouselInitialized === 'true') return;

    const track = section.querySelector('[data-review-video-carousel-track]');
    const prevButton = section.querySelector('[data-review-video-nav="prev"]');
    const nextButton = section.querySelector('[data-review-video-nav="next"]');
    const originalSlides = Array.from(track ? track.children : []).filter(function (slide) {
      return slide.hasAttribute('data-review-video-carousel-slide') && !slide.hasAttribute('data-clone');
    });

    if (!track || !originalSlides.length) return;

    section.dataset.reviewVideoCarouselInitialized = 'true';

    const soundState = new Map();
    const originalCount = originalSlides.length;
    const reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
    const autoSlideEnabled = section.dataset.autoSlideEnabled === 'true';
    const autoSlideInterval = Math.max(
      2000,
      Number(section.dataset.autoSlideInterval) || 4000
    );
    const autoSlideResumeDelay = Math.max(
      1000,
      Number(section.dataset.autoSlideResumeDelay) || 4000
    );
    const pauseAutoSlideOnHover = section.dataset.autoSlidePauseHover !== 'false';

    let slides = [];
    let activeSlide = null;
    let ticking = false;
    let isJumping = false;
    let resizeFrame = null;
    let autoSlideTimer = null;
    let resumeTimer = null;
    let isHovered = false;
    let isFocused = false;
    let isInteracting = false;

    function pauseVideo(video) {
      if (!video) return;

      video.pause();
      video.muted = true;
      video.defaultMuted = true;
      video.setAttribute('muted', 'muted');
    }

    function playVideo(video, shouldUnmute) {
      if (!video) return;

      video.pause();
      video.muted = !shouldUnmute;
      video.defaultMuted = !shouldUnmute;
      video.playsInline = true;
      video.loop = true;
      video.preload = 'auto';
      video.setAttribute('playsinline', '');
      video.setAttribute('preload', 'auto');

      if (shouldUnmute) {
        video.removeAttribute('muted');
        video.volume = 1;
      } else {
        video.setAttribute('muted', 'muted');
      }

      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(function () {});
      }
    }

    function getClosestSlide() {
      const trackRect = track.getBoundingClientRect();
      const trackCenter = trackRect.left + trackRect.width / 2;
      let closest = slides[0];
      let smallestDistance = Infinity;

      slides.forEach(function (slide) {
        const slideRect = slide.getBoundingClientRect();
        const slideCenter = slideRect.left + slideRect.width / 2;
        const distance = Math.abs(trackCenter - slideCenter);

        if (distance < smallestDistance) {
          smallestDistance = distance;
          closest = slide;
        }
      });

      return closest;
    }

    function getCanonicalSlide(originalIndex) {
      return (
        slides[originalCount + originalIndex] ||
        slides.find(function (slide) {
          return (
            slide.dataset.reviewVideoIndex === String(originalIndex) &&
            !slide.hasAttribute('data-clone')
          );
        }) ||
        null
      );
    }

    function setSlideActiveState(nextActiveSlide) {
      activeSlide = nextActiveSlide;

      slides.forEach(function (slide) {
        const isActive = slide === activeSlide;
        slide.classList.toggle('is-active', isActive);

        const video = slide.querySelector('video');
        if (!video) return;

        if (isActive) {
          const originalIndex = Number(slide.dataset.reviewVideoIndex);
          playVideo(video, soundState.get(originalIndex) === true);
        } else {
          pauseVideo(video);
        }
      });
    }

    function updateSoundButtons(originalIndex) {
      const shouldUnmute = soundState.get(originalIndex) === true;

      slides.forEach(function (slide) {
        if (slide.dataset.reviewVideoIndex !== String(originalIndex)) return;

        const soundButton = slide.querySelector('[data-review-video-sound]');
        if (!soundButton) return;

        soundButton.classList.toggle('is-unmuted', shouldUnmute);
        soundButton.classList.toggle('is-muted', !shouldUnmute);
        soundButton.setAttribute('aria-pressed', shouldUnmute ? 'true' : 'false');
        soundButton.setAttribute('aria-label', shouldUnmute ? 'Mute video' : 'Unmute video');
      });
    }

    function syncButtonStates() {
      for (let index = 0; index < originalCount; index += 1) {
        updateSoundButtons(index);
      }
    }

    function centerSlide(slide, behavior) {
      if (!slide) return;

      const left = slide.offsetLeft - track.clientWidth / 2 + slide.clientWidth / 2;
      track.scrollTo({
        left: left,
        behavior: behavior || 'smooth'
      });
    }

    function activateSlide(slide, behavior) {
      if (!slide) return;

      setSlideActiveState(slide);

      if (slide !== activeSlide || behavior === 'auto') {
        centerSlide(slide, behavior);
      } else {
        centerSlide(slide, behavior);
      }

      requestActiveUpdate();
    }

    function normalizeLoopPosition() {
      if (!activeSlide || !activeSlide.hasAttribute('data-clone')) return;

      const originalIndex = Number(activeSlide.dataset.reviewVideoIndex);
      const canonicalSlide = getCanonicalSlide(originalIndex);

      if (!canonicalSlide) return;

      isJumping = true;
      centerSlide(canonicalSlide, 'auto');
      setSlideActiveState(canonicalSlide);

      window.requestAnimationFrame(function () {
        isJumping = false;
        setActiveSlide();
      });
    }

    function setActiveSlide() {
      if (isJumping) {
        ticking = false;
        return;
      }

      setSlideActiveState(getClosestSlide());

      if (activeSlide) {
        normalizeLoopPosition();
      }

      ticking = false;
    }

    function requestActiveUpdate() {
      if (ticking) return;

      ticking = true;
      window.requestAnimationFrame(setActiveSlide);
    }

    function clearAutoSlideTimer() {
      if (!autoSlideTimer) return;

      window.clearTimeout(autoSlideTimer);
      autoSlideTimer = null;
    }

    function clearResumeTimer() {
      if (!resumeTimer) return;

      window.clearTimeout(resumeTimer);
      resumeTimer = null;
    }

    function canAutoSlide() {
      return (
        autoSlideEnabled &&
        !reducedMotionMedia.matches &&
        !document.hidden &&
        !isHovered &&
        !isFocused &&
        !isInteracting &&
        originalCount > 1
      );
    }

    function scheduleAutoSlide(delay) {
      clearAutoSlideTimer();

      if (!canAutoSlide()) return;

      autoSlideTimer = window.setTimeout(advanceAutoSlide, delay || autoSlideInterval);
    }

    function getNextSlide() {
      const currentSlide = activeSlide || getCanonicalSlide(0) || slides[originalCount] || slides[0];
      const currentIndex = slides.indexOf(currentSlide);

      if (currentIndex === -1) {
        return getCanonicalSlide(0) || slides[originalCount] || slides[0];
      }

      return slides[currentIndex + 1] || slides[originalCount] || slides[0];
    }

    function getPreviousSlide() {
      const currentSlide = activeSlide || getCanonicalSlide(0) || slides[originalCount] || slides[0];
      const currentIndex = slides.indexOf(currentSlide);

      if (currentIndex === -1) {
        return getCanonicalSlide(originalCount - 1) || slides[originalCount - 1] || slides[slides.length - 1];
      }

      return (
        slides[currentIndex - 1] ||
        slides[slides.length - 1] ||
        getCanonicalSlide(originalCount - 1) ||
        slides[0]
      );
    }

    function advanceAutoSlide() {
      if (!canAutoSlide()) return;

      const nextSlide = getNextSlide();
      if (!nextSlide) return;

      activateSlide(nextSlide, 'smooth');
      scheduleAutoSlide();
    }

    function pauseAutoSlideTemporarily(delay) {
      if (!autoSlideEnabled) return;

      isInteracting = true;
      clearAutoSlideTimer();
      clearResumeTimer();

      resumeTimer = window.setTimeout(function () {
        isInteracting = false;
        scheduleAutoSlide(260);
      }, delay || autoSlideResumeDelay);
    }

    function handleAutoSlideEnvironmentChange() {
      clearAutoSlideTimer();
      isHovered = pauseAutoSlideOnHover && typeof track.matches === 'function' ? track.matches(':hover') : false;
      isFocused = track.contains(document.activeElement);

      if (document.hidden || reducedMotionMedia.matches) {
        clearResumeTimer();
        isInteracting = false;
      }

      scheduleAutoSlide(260);
    }

    function removeExistingClones() {
      track.querySelectorAll('[data-clone="true"]').forEach(function (clone) {
        clone.remove();
      });
    }

    function cloneSlides() {
      removeExistingClones();

      const prependFragment = document.createDocumentFragment();
      const appendFragment = document.createDocumentFragment();

      function prepareClone(clone) {
        clone.setAttribute('data-clone', 'true');

        clone.querySelectorAll('[id]').forEach(function (element) {
          element.removeAttribute('id');
        });
      }

      originalSlides.forEach(function (slide) {
        const prependClone = slide.cloneNode(true);
        prepareClone(prependClone);
        prependFragment.appendChild(prependClone);

        const appendClone = slide.cloneNode(true);
        prepareClone(appendClone);
        appendFragment.appendChild(appendClone);
      });

      track.insertBefore(prependFragment, track.firstChild);
      track.appendChild(appendFragment);

      slides = Array.from(track.querySelectorAll('[data-review-video-carousel-slide]'));
    }

    function bindControls() {
      function isInteractiveTarget(target) {
        return !!target.closest('a, button, input, select, textarea, label, summary, details, product-form, form');
      }

      section.addEventListener('click', function (event) {
        const soundButton = event.target.closest('[data-review-video-sound]');
        if (soundButton) {
          event.preventDefault();
          event.stopPropagation();
          pauseAutoSlideTemporarily();

          const slide = soundButton.closest('[data-review-video-carousel-slide]');
          const originalIndex = Number(slide.dataset.reviewVideoIndex);
          const shouldUnmute = soundState.get(originalIndex) !== true;

          soundState.set(originalIndex, shouldUnmute);
          updateSoundButtons(originalIndex);

          if (slide === activeSlide) {
            setSlideActiveState(slide);
          } else {
            activateSlide(slide, 'smooth');
          }
          return;
        }

        if (isInteractiveTarget(event.target)) return;

        const slide = event.target.closest('[data-review-video-carousel-slide]');
        if (!slide || !track.contains(slide)) return;

        pauseAutoSlideTemporarily();
        activateSlide(slide, 'smooth');
      });
    }

    function bindDesktopNav() {
      const shouldDisable = originalCount < 2;

      [prevButton, nextButton].forEach(function (button) {
        if (!button) return;
        button.disabled = shouldDisable;
      });

      if (prevButton && prevButton.dataset.reviewVideoNavBound !== 'true') {
        prevButton.dataset.reviewVideoNavBound = 'true';
        prevButton.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation();
          pauseAutoSlideTemporarily();

          const previousSlide = getPreviousSlide();
          if (previousSlide) {
            activateSlide(previousSlide, 'smooth');
          }
        });
      }

      if (nextButton && nextButton.dataset.reviewVideoNavBound !== 'true') {
        nextButton.dataset.reviewVideoNavBound = 'true';
        nextButton.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation();
          pauseAutoSlideTemporarily();

          const nextSlide = getNextSlide();
          if (nextSlide) {
            activateSlide(nextSlide, 'smooth');
          }
        });
      }
    }

    function prepareVideos() {
      slides.forEach(function (slide) {
        const video = slide.querySelector('video');
        if (!video) return;

        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.loop = true;
        video.preload = 'auto';
        video.setAttribute('muted', 'muted');
        video.setAttribute('playsinline', '');
        video.setAttribute('preload', 'auto');
      });
    }

    cloneSlides();
    prepareVideos();
    syncButtonStates();
    bindControls();
    bindDesktopNav();

    track.addEventListener('scroll', requestActiveUpdate, { passive: true });
    track.addEventListener('pointerdown', function () {
      pauseAutoSlideTemporarily();
    });
    track.addEventListener(
      'wheel',
      function () {
        pauseAutoSlideTemporarily();
      },
      { passive: true }
    );
    track.addEventListener('touchstart', function () {
      pauseAutoSlideTemporarily();
    }, { passive: true });

    if (pauseAutoSlideOnHover) {
      track.addEventListener('mouseenter', function () {
        isHovered = true;
        clearAutoSlideTimer();
      });

      track.addEventListener('mouseleave', function () {
        isHovered = false;
        scheduleAutoSlide(260);
      });
    }

    track.addEventListener('focusin', function () {
      isFocused = true;
      clearAutoSlideTimer();
    });

    track.addEventListener('focusout', function () {
      window.setTimeout(function () {
        if (!track.contains(document.activeElement)) {
          isFocused = false;
          scheduleAutoSlide(260);
        }
      }, 0);
    });

    document.addEventListener('visibilitychange', handleAutoSlideEnvironmentChange);

    if (typeof reducedMotionMedia.addEventListener === 'function') {
      reducedMotionMedia.addEventListener('change', handleAutoSlideEnvironmentChange);
    } else if (typeof reducedMotionMedia.addListener === 'function') {
      reducedMotionMedia.addListener(handleAutoSlideEnvironmentChange);
    }

    window.addEventListener('resize', function () {
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(function () {
        const currentIndex = activeSlide ? Number(activeSlide.dataset.reviewVideoIndex) : 0;
        const canonicalSlide = getCanonicalSlide(currentIndex) || slides[originalCount] || slides[0];

        centerSlide(canonicalSlide, 'auto');
        setSlideActiveState(canonicalSlide);
        requestActiveUpdate();
        scheduleAutoSlide(260);
      });
    });

    centerSlide(getCanonicalSlide(0) || slides[originalCount] || slides[0], 'auto');
    window.setTimeout(function () {
      setActiveSlide();
    }, 80);
    scheduleAutoSlide();
  }

  function initAllReviewVideoCarousels() {
    document
      .querySelectorAll('[data-review-video-carousel]')
      .forEach(initReviewVideoCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllReviewVideoCarousels);
  } else {
    initAllReviewVideoCarousels();
  }

  document.addEventListener('shopify:section:load', function (event) {
    const section = event.target.querySelector('[data-review-video-carousel]');

    if (section) {
      section.dataset.reviewVideoCarouselInitialized = 'false';
      initReviewVideoCarousel(section);
    }
  });
})();
